import { featureTogglesInGql } from 'config/featureToggles/featureToggles.utils';
import { useAppConfig } from './useAppConfig';
import { useAuthSession } from './useAuthSession';
import { graphqlFetcher } from '../services/graphqlFetcher';
import { PiTargetBg } from '../config/appConfig/appConfig.types';
import { useFeatureToggles } from './useFeatureToggles';

interface IGraphqlFetcherQueryFnProps {
  query: string;
  graphql: string;
  authToken: string;
  targetBg: PiTargetBg;
  useDirectHost?: boolean;
  legacyToken?: string;
}

export const graphqlFetcherQueryFn = <TData, TVariables>({
  query,
  graphql,
  authToken,
  targetBg,
  useDirectHost,
  legacyToken,
}: IGraphqlFetcherQueryFnProps) => {
  return async (variables?: TVariables): Promise<TData> => {
    const additionalHeaders = new Map<string, string>();
    if (useDirectHost && legacyToken) {
      additionalHeaders.set('Authorization', legacyToken);
    }

    return graphqlFetcher({
      endpoint: graphql,
      accessToken: authToken ?? '',
      targetBg,
      query,
      variables,
      additionalHeaders: Object.fromEntries(additionalHeaders),
    });
  };
};

/**
 * GraphQL Fetcher hooks that's injected into all the graphql Generated Request hooks
 * via `src/graphql/codegen.yml` upon `yarn generate`
 */
export function useGraphqlFetcher<TData, TVariables>(query: string) {
  const { legacyToken, authToken } = useAuthSession();
  const { isFeatureToggledOn } = useFeatureToggles();
  const {
    api: { useDirectHost, graphql },
    auth: { targetBg },
  } = useAppConfig();

  return async (variables?: TVariables): Promise<TData> => {
    const additionalHeaders = new Map<string, string>();
    if (useDirectHost && legacyToken) {
      additionalHeaders.set('Authorization', legacyToken);
    }

    return graphqlFetcher({
      endpoint: graphql,
      accessToken: authToken ?? '',
      targetBg,
      query: featureTogglesInGql(query, isFeatureToggledOn),
      variables,
      additionalHeaders: Object.fromEntries(additionalHeaders),
    });
  };
}
